import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Pagination,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import * as XLSX from "xlsx";
import { JsonDiffViewer } from "./JsonViewer";

const AuditLogsList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [modelFilter, setModelFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Build query params
  const [queryParams, setQueryParams] = useState("");

  useEffect(() => {
    let params = `page=${page}&page_size=10`;
    if (searchTerm) {
      params += `&search=${encodeURIComponent(searchTerm)}`;
    }
    if (actionFilter !== "all") {
      params += `&action=${actionFilter}`;
    }
    if (modelFilter !== "all") {
      params += `&model=${modelFilter}`;
    }
    if (userFilter !== "all") {
      params += `&actor_username=${encodeURIComponent(userFilter)}`;
    }
    if (dateRange !== "all") {
      const now = new Date();
      let startDate = new Date();
      switch (dateRange) {
        case "today":
          startDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          startDate.setDate(now.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          break;
        case "month":
          startDate.setMonth(now.getMonth() - 1);
          startDate.setHours(0, 0, 0, 0);
          break;
        default:
          break;
      }
      params += `&created_at__gte=${startDate.toISOString()}`;
    }
    setQueryParams(params);
  }, [page, searchTerm, actionFilter, modelFilter, userFilter, dateRange]);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["audit-logs", queryParams],
    `audit-logs/?${queryParams}`,
    token,
    { enabled: !!token }
  );

  // Get unique values for filters
  const { data: allLogs } = useFetchObjects(
    ["audit-logs-all"],
    "audit-logs/?page_size=1000",
    token,
    { enabled: !!token }
  );

  const allLogsData = Array.isArray(allLogs) ? allLogs : allLogs?.results || [];
  const uniqueModels = [
    ...new Set(
      allLogsData.map((log) => `${log.app_label}.${log.model}`).filter(Boolean)
    ),
  ].sort();
  const uniqueUsers = [
    ...new Set(allLogsData.map((log) => log.actor_username).filter(Boolean)),
  ].sort();

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setOpenDialog(true);
  };

  const toggleRow = (logId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const getActionColor = (action) => {
    const colors = {
      create: "success",
      update: "info",
      soft_delete: "warning",
      restore: "primary",
      hard_delete: "error",
    };
    return colors[action] || "default";
  };

  const handleExport = (exportFormat) => {
    const logs = Array.isArray(data) ? data : data?.results || [];
    const exportData = logs.map((log) => ({
      Date: log.created_at
        ? format(parseISO(log.created_at), "yyyy-MM-dd HH:mm:ss")
        : "-",
      Action: t(`audit.action.${log.action}`) || log.action,
      Model: `${log.app_label}.${log.model}`,
      Object: log.object_repr || log.object_pk,
      User: log.actor_username || "-",
      IP: log.ip_address || "-",
    }));

    if (exportFormat === "csv") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Audit Logs");
      XLSX.writeFile(wb, `audit-logs-${new Date().toISOString()}.csv`);
    } else if (exportFormat === "xlsx") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Audit Logs");
      XLSX.writeFile(wb, `audit-logs-${new Date().toISOString()}.xlsx`);
    }
  };

  // Handle both paginated and non-paginated responses
  const logs = Array.isArray(data) ? data : data?.results || [];
  const totalCount = data?.count || logs.length;

  const clearFilters = () => {
    setActionFilter("all");
    setModelFilter("all");
    setUserFilter("all");
    setDateRange("all");
    setSearchTerm("");
    setPage(1);
  };

  const activeFiltersCount =
    (actionFilter !== "all" ? 1 : 0) +
    (modelFilter !== "all" ? 1 : 0) +
    (userFilter !== "all" ? 1 : 0) +
    (dateRange !== "all" ? 1 : 0) +
    (searchTerm ? 1 : 0);

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Grid>
    );
  }

  if (isError) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{t("audit.errorLoadingLogs")}</Typography>
      </Grid>
    );
  }

  return (
    <Grid container maxWidth="xl" mb={3}>
      {/* Filters and Search */}
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t("audit.searchLogs")}
              variant="outlined"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("audit.action")}</InputLabel>
              <Select
                value={actionFilter}
                label={t("audit.action")}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">{t("audit.all")}</MenuItem>
                <MenuItem value="create">{t("audit.action.create")}</MenuItem>
                <MenuItem value="update">{t("audit.action.update")}</MenuItem>
                <MenuItem value="soft_delete">
                  {t("audit.action.soft_delete")}
                </MenuItem>
                <MenuItem value="restore">{t("audit.action.restore")}</MenuItem>
                <MenuItem value="hard_delete">
                  {t("audit.action.hard_delete")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("audit.model")}</InputLabel>
              <Select
                value={modelFilter}
                label={t("audit.model")}
                onChange={(e) => {
                  setModelFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">{t("audit.all")}</MenuItem>
                {uniqueModels.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("audit.timeRange")}</InputLabel>
              <Select
                value={dateRange}
                label={t("audit.timeRange")}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">{t("audit.allTime")}</MenuItem>
                <MenuItem value="today">{t("audit.today")}</MenuItem>
                <MenuItem value="week">{t("audit.lastWeek")}</MenuItem>
                <MenuItem value="month">{t("audit.lastMonth")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title={t("audit.export")}>
                <IconButton
                  onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  color="primary"
                >
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
              {activeFiltersCount > 0 && (
                <Tooltip title={t("audit.clearFilters")}>
                  <IconButton onClick={clearFilters} color="secondary">
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
        </Grid>
        {activeFiltersCount > 0 && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={`${activeFiltersCount} ${t("audit.filtersActive")}`}
              onDelete={clearFilters}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Grid>

      {/* Export Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExport("csv")}>
          {t("audit.exportCSV")}
        </MenuItem>
        <MenuItem onClick={() => handleExport("xlsx")}>
          {t("audit.exportExcel")}
        </MenuItem>
      </Menu>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("audit.date")}</TableCell>
              <TableCell>{t("audit.action")}</TableCell>
              <TableCell>{t("audit.model")}</TableCell>
              <TableCell>{t("audit.object")}</TableCell>
              <TableCell>{t("audit.actor")}</TableCell>
              <TableCell>{t("audit.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    {t("audit.noLogsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <React.Fragment key={log.id}>
                  <TableRow hover>
                    <TableCell>
                      {log.created_at
                        ? format(parseISO(log.created_at), "yyyy-MM-dd HH:mm:ss")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`audit.action.${log.action}`)}
                        color={getActionColor(log.action)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {log.app_label}.{log.model}
                    </TableCell>
                    <TableCell>{log.object_repr || log.object_pk}</TableCell>
                    <TableCell>{log.actor_username || "-"}</TableCell>
                    <TableCell>
                      <Tooltip title={t("audit.viewDetails")}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(log)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(log.id)}
                      >
                        {expandedRows[log.id] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {expandedRows[log.id] && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t("audit.changes")}:
                          </Typography>
                          <JsonDiffViewer
                            before={log.changes_before}
                            after={log.changes_after}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalCount > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(totalCount / 10)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{t("audit.logDetails")}</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("audit.date")}:</strong>{" "}
                    {selectedLog.created_at
                      ? format(parseISO(selectedLog.created_at), "yyyy-MM-dd HH:mm:ss")
                      : "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("audit.action")}:</strong>{" "}
                    <Chip
                      label={t(`audit.action.${selectedLog.action}`)}
                      color={getActionColor(selectedLog.action)}
                      size="small"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("audit.model")}:</strong>{" "}
                    {selectedLog.app_label}.{selectedLog.model}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("audit.object")}:</strong>{" "}
                    {selectedLog.object_repr || selectedLog.object_pk}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("audit.actor")}:</strong>{" "}
                    {selectedLog.actor_username || "-"}
                  </Typography>
                </Grid>
                {selectedLog.ip_address && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom>
                      <strong>{t("audit.ipAddress")}:</strong>{" "}
                      {selectedLog.ip_address}
                    </Typography>
                  </Grid>
                )}
                {selectedLog.integrity_hash && (
                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                      <strong>{t("audit.integrityHash")}:</strong>{" "}
                      <code
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#f5f5f5",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          display: "inline-block",
                        }}
                      >
                        {selectedLog.integrity_hash}
                      </code>
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {(selectedLog.changes_before || selectedLog.changes_after) && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    {t("audit.changes")}:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      maxHeight: "500px",
                      overflow: "auto",
                    }}
                  >
                    <JsonDiffViewer
                      before={selectedLog.changes_before}
                      after={selectedLog.changes_after}
                    />
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t("audit.close")}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default AuditLogsList;
